// apps/web/src/lib/painel/home/esteira-tom.ts — Fase 17.8, Plano 08 (UI-SPEC U-06).
// Funções puras da esteira "Fluxo agora": o tom de cada etapa entre as tintas do par do perfil (só `var()` e percentuais, nenhum hex),
// os rótulos de unidade e o texto da nota do gargalo. Sem React, para ser reaproveitado pelas esteiras dos demais perfis.
import type { CSSProperties } from 'react';
import type { UnidadeMetrica } from '@pieltec/contracts';

/**
 * Percentual da tinta 1 do par do perfil para a etapa `i` (0 = primeira) de `n`: `t = 1 - i/(n-1)`. A primeira etapa usa 100% da
 * tinta 1, a última 0% (tinta 2). Com uma única etapa não há gradiente: 100.
 */
export function tomDaEtapa(i: number, n: number): number {
  if (n <= 1) return 100;
  const t = 1 - i / (n - 1);
  return Math.round(Math.min(1, Math.max(0, t)) * 1000) / 10;
}

/** Custom properties `--sc` (tinta do texto) e `--sc-fill` (preenchimento do anel) da etapa: misturas de `var(--perfil-*)`. */
export function estiloDaEtapa(i: number, n: number): CSSProperties {
  const p = tomDaEtapa(i, n);
  return {
    '--sc': `color-mix(in srgb, var(--perfil-1-ink) ${p}%, var(--perfil-2-ink))`,
    '--sc-fill': `color-mix(in srgb, var(--perfil-1) ${p}%, var(--perfil-2))`,
  } as CSSProperties;
}

const UNIDADES: Readonly<Partial<Record<UnidadeMetrica, readonly [string, string]>>> = {
  lotes: ['lote', 'lotes'],
  ops: ['OP', 'OPs'],
  pvs: ['PV', 'PVs'],
  itens: ['item', 'itens'],
  notas: ['nota', 'notas'],
  pedidos: ['pedido', 'pedidos'],
  ncs: ['NC', 'NCs'],
  materiais: ['material', 'materiais'],
  clientes: ['cliente', 'clientes'],
  expedicoes: ['expedição', 'expedições'],
  pc: ['peça', 'peças'],
};

/** "1 lote", "5 lotes": a unidade no singular só com exatamente 1. */
export function rotuloUnidade(unidade: UnidadeMetrica, n: number): string {
  const formas = UNIDADES[unidade];
  const nome = formas === undefined ? unidade : n === 1 ? formas[0] : formas[1];
  return `${n.toLocaleString('pt-BR')} ${nome}`;
}

/** "F1", "F1 e F2", "F1, F2 e F3". */
export function listaPtBr(itens: readonly string[]): string {
  if (itens.length <= 1) return itens[0] ?? '';
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1] as string}`;
}

/** Limite de "parado" mostrado na nota (o mesmo LIMITE_HORAS_PARADO = 24 da API, que filtra os cartões). */
export const LIMITE_HORAS_PARADO_ROTULO = 24;

export interface PartesDaNota {
  /** Nome da etapa (vai em negrito). */
  readonly etapa: string;
  /** Texto depois do nome: " concentra a fila: 12 lotes parados há mais de 24 h, vindos de F1, F2 e F3." */
  readonly resto: string;
}

/** Unidades femininas: a nota do gargalo concorda "paradas" (3 notas paradas), e não "parados". */
const UNIDADES_FEMININAS: ReadonlySet<UnidadeMetrica> = new Set<UnidadeMetrica>(['notas', 'expedicoes', 'ncs']);

/**
 * Frase de uma linha do gargalo. Estilo `concentra` (padrão, PCP): "A **Cura** concentra a fila: 12 lotes parados há mais de 24 h,
 * vindos de F1, F2 e F3."; estilo `gargalo` (etapas sem artigo natural, como "A fechar" e "Aprovado CQ"): "Gargalo em **Aprovação**:
 * 3 notas paradas há mais de 24 h.". Sem origens a oração "vindos de" é omitida.
 */
export function partesDaNota(
  nota: { readonly etapa: string; readonly parados: number; readonly origens: readonly string[] },
  unidade: UnidadeMetrica,
  estilo: 'concentra' | 'gargalo' = 'concentra',
  limiteParado: string = `${LIMITE_HORAS_PARADO_ROTULO} h`,
): PartesDaNota {
  const parado = UNIDADES_FEMININAS.has(unidade) ? 'parada' : 'parado';
  const contagem = `${rotuloUnidade(unidade, nota.parados)} ${nota.parados === 1 ? parado : `${parado}s`} há mais de ${limiteParado}`;
  const origens = nota.origens.length === 0 ? '' : `, vindos de ${listaPtBr(nota.origens)}`;
  return { etapa: nota.etapa, resto: estilo === 'gargalo' ? `: ${contagem}${origens}.` : ` concentra a fila: ${contagem}${origens}.` };
}
